import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Loader } from 'rsuite';
import { setPendingPriceListRerun } from '../../../../../hooks/usePriceListRerunNotification';
import {
  getPriceListRerunAvailability,
  isPriceListRerunRunning,
  PRICE_LIST_RERUN_AVAILABILITY_QUERY_KEY,
  PriceListRerunResponse,
  priceListRerunCandidates,
  previewPriceListRerun,
  startPriceListRerun,
} from '../../../../../services/PriceListRerunService';
import { buildRerunCardContent, describeRerunRefusal, rerunRefetchInterval } from './priceListRerunContent';
import { PriceListRerunDialogs } from './PriceListRerunDialogs';
import { RerunCountList } from './RerunCountList';
import { RerunDialogState } from './types';
import styles from './PriceListRerunCard.module.scss';

/**
 * Reprocessamento da tabela de preços no meio do dia: só aparece para cliente liberado; mostra se dá para
 * reprocessar hoje, a prévia antes de confirmar e o resultado do dia. O aviso de fim sai em qualquer tela
 * (usePriceListRerunNotification).
 */
export const PriceListRerunCard = () => {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<RerunDialogState>(null);

  const { data: availability } = useQuery({
    queryKey: PRICE_LIST_RERUN_AVAILABILITY_QUERY_KEY,
    queryFn: getPriceListRerunAvailability,
    refetchInterval: rerunRefetchInterval,
  });

  if (!availability?.enabled) return null;

  const content = buildRerunCardContent(availability);
  const running = isPriceListRerunRunning(availability.execution?.status);
  const closeDialog = () => setDialog(null);

  const openPreview = async () => {
    setDialog({ type: 'previewing' });
    try {
      const preview = await previewPriceListRerun();
      if (preview.httpStatus >= 300) {
        setDialog({ type: 'error', message: describeRerunRefusal(preview) });
        return;
      }
      setDialog({ type: priceListRerunCandidates(preview) > 0 ? 'confirm' : 'nothing', preview });
    } catch {
      setDialog({ type: 'error', message: describeRerunRefusal({ httpStatus: 0 }) });
    }
  };

  const startRerun = async (preview: PriceListRerunResponse) => {
    setDialog({ type: 'starting', preview });
    try {
      const started = await startPriceListRerun();
      if (started.httpStatus >= 300 || !started.executionId) {
        setDialog({ type: 'error', message: describeRerunRefusal(started) });
        return;
      }
      setPendingPriceListRerun(started.executionId);
      setDialog(null);
    } catch {
      setDialog({ type: 'error', message: describeRerunRefusal({ httpStatus: 0 }) });
    } finally {
      void queryClient.invalidateQueries({ queryKey: PRICE_LIST_RERUN_AVAILABILITY_QUERY_KEY });
    }
  };

  return (
    <>
      <section className={styles.card} aria-label="Reprocessar a tabela de preços">
        <div className={styles.header}>
          <h2>Reprocessar a tabela hoje</h2>
          <span className={`${styles.badge} ${styles[content.tone]}`}>{content.badge}</span>
        </div>

        <p className={styles.text}>{content.text}</p>

        {running ? (
          <div className={styles.progress}>
            <Loader size="xs" />
            <span>Atualizando o Gerenciador...</span>
          </div>
        ) : null}

        {content.counts ? <RerunCountList items={content.counts} /> : null}

        {!availability.execution ? (
          <>
            <Button
              className={styles.button}
              appearance="primary"
              id="price-list-rerun-btn"
              disabled={!availability.canStart}
              loading={dialog?.type === 'previewing'}
              onClick={openPreview}
            >
              REPROCESSAR TABELA
            </Button>
            <p className={styles.footnote}>Disponível uma vez por dia, depois que o motor de precificação termina.</p>
          </>
        ) : null}
      </section>

      <PriceListRerunDialogs dialog={dialog} onClose={closeDialog} onConfirm={startRerun} />
    </>
  );
};
